import React, { useState, useEffect } from 'react';
import { 
  X, 
  Home, 
  Calendar, 
  Check, 
  Users, 
  AlertCircle, 
  Sparkles, 
  Search,
  CheckSquare,
  Square
} from 'lucide-react';
import api from '../services/api';

const BulkGrantModal = ({ 
  isOpen, 
  onClose, 
  defaultType = 'WFH', // 'WFH' or 'LEAVE'
  onSuccess,
  preloadedEmployees = null, // optional list of team members if opened from manager page
  title = 'Allocate WFH or Leave',
  subtitle = 'Assign Work From Home or Leave to all or selected employees',
  badgeText = 'Direct Allocation'
}) => {
  const [grantType, setGrantType] = useState(defaultType); // 'WFH' or 'LEAVE'
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [selectedEmpIds, setSelectedEmpIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('ALL');
  
  // Form state
  const [startDate, setStartDate] = useState(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  });
  const [reason, setReason] = useState('Approved by Management');
  const [wfhPaymentStatus, setWfhPaymentStatus] = useState('FULL_DAY_PAID');
  
  // Leaves specific
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [selectedLeaveTypeId, setSelectedLeaveTypeId] = useState('');
  const [isPaidLeave, setIsPaidLeave] = useState(true);

  // Submission state
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    if (isOpen) {
      setGrantType(defaultType);
      setErrorMsg('');
      setSuccessMsg('');
      loadEmployeesAndTypes();
    }
  }, [isOpen, defaultType]);

  const loadEmployeesAndTypes = async () => {
    setLoadingEmployees(true);
    try {
      if (preloadedEmployees && preloadedEmployees.length > 0) {
        setEmployees(preloadedEmployees);
        setSelectedEmpIds(preloadedEmployees.map(e => e._id));
      } else {
        let empRes;
        try {
          empRes = await api.get('/employees');
        } catch (fetchErr) {
          // If user does not have permission to fetch all employees (e.g., Manager/Employee), fall back to team
          empRes = await api.get('/employees/team');
        }
        // Filter out inactive if desired
        const activeEmps = (empRes.data || []).filter(e => e.isActive !== false);
        setEmployees(activeEmps);
        // By default select all active employees
        setSelectedEmpIds(activeEmps.map(e => e._id));
      }

      // Fetch leave types
      try {
        const ltRes = await api.get('/leaves/types');
        const types = ltRes.data || [];
        setLeaveTypes(types);
        if (types.length > 0) {
          const defaultPaid = types.find(t => t.name === 'Paid Leave') || types.find(t => t.isPaid) || types[0];
          setSelectedLeaveTypeId(defaultPaid._id);
          setIsPaidLeave(defaultPaid.isPaid !== false);
        }
      } catch (ltErr) {
        console.error('Failed to load leave types', ltErr);
      }
    } catch (err) {
      console.error('Failed to load employees', err);
      setErrorMsg('Failed to load employee list');
    } finally {
      setLoadingEmployees(false);
    }
  };

  if (!isOpen) return null;

  // Filtered employees list
  const departments = ['ALL', ...new Set(employees.map(e => e.department?.name).filter(Boolean))];
  const filteredEmployees = employees.filter(emp => {
    const term = searchTerm.toLowerCase();
    const fullName = `${emp.firstName || ''} ${emp.lastName || ''}`.toLowerCase();
    const empId = (emp.employeeId || '').toLowerCase();
    const matchesSearch = fullName.includes(term) || empId.includes(term);
    const matchesDept = departmentFilter === 'ALL' || emp.department?.name === departmentFilter;
    return matchesSearch && matchesDept;
  });

  const allFilteredSelected = filteredEmployees.length > 0 && filteredEmployees.every(e => selectedEmpIds.includes(e._id));

  const handleToggleSelectAll = () => {
    if (allFilteredSelected) {
      // Unselect filtered employees
      const filteredIds = new Set(filteredEmployees.map(e => e._id));
      setSelectedEmpIds(prev => prev.filter(id => !filteredIds.has(id)));
    } else {
      // Select all filtered employees
      const newIds = new Set([...selectedEmpIds, ...filteredEmployees.map(e => e._id)]);
      setSelectedEmpIds(Array.from(newIds));
    }
  };

  const handleToggleEmployee = (id) => {
    setSelectedEmpIds(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const calculateDays = () => {
    if (!startDate || !endDate) return 0;
    const s = new Date(startDate);
    const e = new Date(endDate);
    if (e < s) return 0;
    return Math.ceil((e - s) / (1000 * 60 * 60 * 24)) + 1;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    setSuccessMsg('');

    if (selectedEmpIds.length === 0) {
      setErrorMsg('Please select at least one employee.');
      return;
    }
    if (!startDate || !endDate) {
      setErrorMsg('Please specify valid start and end dates.');
      return;
    }
    if (new Date(endDate) < new Date(startDate)) {
      setErrorMsg('End date cannot be earlier than start date.');
      return;
    }

    setSubmitting(true);
    try {
      if (grantType === 'WFH') {
        const payload = {
          employeeIds: selectedEmpIds,
          startDate,
          endDate,
          reason,
          paymentStatus: wfhPaymentStatus
        };
        const res = await api.post('/attendance/wfh/bulk-grant', payload);
        setSuccessMsg(`Successfully granted WFH to ${res.data.successCount} employee(s)!`);
      } else {
        const payload = {
          employeeIds: selectedEmpIds,
          leaveTypeId: selectedLeaveTypeId,
          startDate,
          endDate,
          reason,
          isPaid: isPaidLeave
        };
        const res = await api.post('/leaves/bulk-grant', payload);
        setSuccessMsg(`Successfully granted leave to ${res.data.successCount} employee(s)!`);
      }

      if (onSuccess) onSuccess();
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      setErrorMsg(err.response?.data?.message || err.message || 'Bulk grant failed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4">
      <div className="bg-white rounded-3xl max-w-2xl w-full shadow-2xl border border-gray-100 overflow-hidden flex flex-col max-h-[90vh] animate-in fade-in zoom-in-95 duration-150">
        
        {/* Header */}
        <div className="px-6 py-4.5 bg-gradient-to-r from-slate-900 via-indigo-950 to-blue-950 text-white flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-white/10 border border-white/20 flex items-center justify-center shadow-inner">
              <Sparkles className="w-5 h-5 text-amber-300" />
            </div>
            <div>
              <h3 className="text-base sm:text-lg font-bold tracking-tight text-white flex items-center gap-2">
                {title}
                <span className="text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-200 border border-blue-400/30">
                  {badgeText}
                </span>
              </h3>
              <p className="text-xs text-blue-200/80 mt-0.5">
                {subtitle}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-300 hover:text-white p-2 rounded-xl hover:bg-white/10 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5 overflow-y-auto flex-1 custom-scrollbar">
          {errorMsg && (
            <div className="p-3.5 bg-rose-50 border border-rose-200 text-rose-700 text-xs rounded-xl flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
              <span>{errorMsg}</span>
            </div>
          )}

          {successMsg && (
            <div className="p-3.5 bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs rounded-xl flex items-center gap-2">
              <Check className="w-4 h-4 text-emerald-600 shrink-0" />
              <span className="font-semibold">{successMsg}</span>
            </div>
          )}

          {/* Action Type Toggle */}
          <div>
            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">
              Select What You Want To Grant
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setGrantType('WFH')}
                className={`flex items-center justify-center gap-2.5 p-3.5 rounded-2xl border-2 font-bold text-sm transition-all ${
                  grantType === 'WFH'
                    ? 'border-indigo-600 bg-indigo-50/70 text-indigo-900 shadow-xs'
                    : 'border-gray-200 hover:border-gray-300 text-gray-600 bg-white'
                }`}
              >
                <Home className={`w-4 h-4 ${grantType === 'WFH' ? 'text-indigo-600' : 'text-gray-400'}`} />
                <span>Work From Home (WFH)</span>
              </button>

              <button
                type="button"
                onClick={() => setGrantType('LEAVE')}
                className={`flex items-center justify-center gap-2.5 p-3.5 rounded-2xl border-2 font-bold text-sm transition-all ${
                  grantType === 'LEAVE'
                    ? 'border-blue-600 bg-blue-50/70 text-blue-900 shadow-xs'
                    : 'border-gray-200 hover:border-gray-300 text-gray-600 bg-white'
                }`}
              >
                <Calendar className={`w-4 h-4 ${grantType === 'LEAVE' ? 'text-blue-600' : 'text-gray-400'}`} />
                <span>Leave / Holiday</span>
              </button>
            </div>
          </div>

          {/* Date Range Selection */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-gray-50/70 p-4 rounded-2xl border border-gray-200/80">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Start Date *
              </label>
              <input
                type="date"
                required
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  if (endDate < e.target.value) setEndDate(e.target.value);
                }}
                className="w-full px-3 py-2 bg-white border border-gray-300 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                End Date *
              </label>
              <input
                type="date"
                required
                value={endDate}
                min={startDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2 bg-white border border-gray-300 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="sm:col-span-2 flex items-center justify-between text-xs text-gray-500 pt-1">
              <span>Duration: <strong className="text-gray-800">{calculateDays()} day(s)</strong></span>
              <span className="text-gray-400 italic">Inclusive of start and end date</span>
            </div>
          </div>

          {/* Conditional Options */}
          {grantType === 'WFH' ? (
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                WFH Compensation Status
              </label>
              <select
                value={wfhPaymentStatus}
                onChange={(e) => setWfhPaymentStatus(e.target.value)}
                className="w-full px-3 py-2 bg-white border border-gray-300 rounded-xl text-sm font-medium focus:ring-2 focus:ring-indigo-500"
              >
                <option value="FULL_DAY_PAID">Full Day Paid (Normal WFH Working Day)</option>
                <option value="HALF_DAY_PAID">Half Day Paid</option>
                <option value="UNPAID">Unpaid WFH</option>
              </select>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Leave Type
                </label>
                <select
                  value={selectedLeaveTypeId}
                  onChange={(e) => {
                    setSelectedLeaveTypeId(e.target.value);
                    const selected = leaveTypes.find(t => t._id === e.target.value);
                    if (selected) setIsPaidLeave(selected.isPaid !== false);
                  }}
                  className="w-full px-3 py-2 bg-white border border-gray-300 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-500"
                >
                  {leaveTypes.map(lt => (
                    <option key={lt._id} value={lt._id}>
                      {lt.name} {lt.isPaid ? '(Paid)' : '(Unpaid)'}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Paid / Unpaid
                </label>
                <div className="flex gap-2 pt-1">
                  <label className="flex items-center gap-1.5 text-xs font-medium text-gray-700 cursor-pointer">
                    <input
                      type="radio"
                      name="isPaidLeave"
                      checked={isPaidLeave}
                      onChange={() => setIsPaidLeave(true)}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    <span>Paid Leave</span>
                  </label>
                  <label className="flex items-center gap-1.5 text-xs font-medium text-gray-700 cursor-pointer ml-3">
                    <input
                      type="radio"
                      name="isPaidLeave"
                      checked={!isPaidLeave}
                      onChange={() => setIsPaidLeave(false)}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    <span>Unpaid Leave</span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Reason */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Reason / Remarks for Employees
            </label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g., Company-wide Remote Work Day / Office Renovation / Festival"
              className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Employee Selection Section */}
          <div className="border border-gray-200 rounded-2xl overflow-hidden bg-white shadow-xs">
            <div className="p-3 bg-gray-50/80 border-b border-gray-200 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2.5">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-gray-600" />
                <span className="text-xs font-bold text-gray-800">
                  Select Employees ({selectedEmpIds.length} / {employees.length} Selected)
                </span>
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleToggleSelectAll}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-700 hover:text-blue-900 bg-blue-50 hover:bg-blue-100 px-2.5 py-1 rounded-lg transition-colors"
                >
                  {allFilteredSelected ? (
                    <>
                      <CheckSquare className="w-3.5 h-3.5 text-blue-600" />
                      <span>Deselect All</span>
                    </>
                  ) : (
                    <>
                      <Square className="w-3.5 h-3.5 text-blue-600" />
                      <span>Select All ({filteredEmployees.length})</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Employee Filters */}
            <div className="p-2.5 border-b border-gray-100 bg-white flex flex-wrap gap-2 items-center">
              <div className="relative flex-1 min-w-[180px]">
                <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-2.5" />
                <input
                  type="text"
                  placeholder="Filter by name or employee ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-8 pr-3 py-1.5 border border-gray-200 rounded-lg text-xs focus:ring-1 focus:ring-blue-500"
                />
              </div>

              {departments.length > 2 && (
                <select
                  value={departmentFilter}
                  onChange={(e) => setDepartmentFilter(e.target.value)}
                  className="px-2.5 py-1.5 border border-gray-200 rounded-lg text-xs text-gray-700 bg-white"
                >
                  {departments.map(d => (
                    <option key={d} value={d}>
                      {d === 'ALL' ? 'All Departments' : d}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Employee Checkbox List */}
            <div className="max-h-48 overflow-y-auto divide-y divide-gray-100 p-1">
              {loadingEmployees ? (
                <div className="p-6 text-center text-xs text-gray-500">Loading employees...</div>
              ) : filteredEmployees.length === 0 ? (
                <div className="p-6 text-center text-xs text-gray-500">No employees match filters</div>
              ) : (
                filteredEmployees.map(emp => {
                  const isChecked = selectedEmpIds.includes(emp._id);
                  return (
                    <label
                      key={emp._id}
                      className={`flex items-center justify-between p-2 rounded-xl cursor-pointer transition-colors ${
                        isChecked ? 'bg-blue-50/60' : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-2.5">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => handleToggleEmployee(emp._id)}
                          className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                        />
                        <div>
                          <div className="text-xs font-semibold text-gray-900">
                            {emp.firstName} {emp.lastName}
                          </div>
                          <div className="text-[10px] text-gray-400">
                            {emp.employeeId || emp.email} • {emp.department?.name || 'General'}
                          </div>
                        </div>
                      </div>

                      <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 capitalize">
                        {emp.designation || emp.role || 'Staff'}
                      </span>
                    </label>
                  );
                })
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="pt-2 flex items-center justify-end gap-3 shrink-0">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2.5 rounded-xl border border-gray-300 font-semibold text-xs text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || selectedEmpIds.length === 0}
              className={`px-5 py-2.5 rounded-xl font-bold text-xs text-white shadow-sm flex items-center gap-2 transition-all ${
                grantType === 'WFH'
                  ? 'bg-indigo-600 hover:bg-indigo-700 shadow-indigo-200'
                  : 'bg-blue-600 hover:bg-blue-700 shadow-blue-200'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {submitting ? (
                <span>Granting...</span>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  <span>
                    Grant {grantType === 'WFH' ? 'WFH' : 'Leave'} ({selectedEmpIds.length} Employees)
                  </span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BulkGrantModal;
